<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import parsePhoneNumber, { getCountries, getCountryCallingCode, AsYouType } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import { ChevronsUpDown } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

interface Props {
  modelValue?: string
  disabled?: boolean
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  disabled: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

// Country selector
const selectedCountry = ref<CountryCode>('US')
const countryPickerOpen = ref(false)

// Phone number input
const phoneInput = ref('')

// Track if user is currently typing to prevent watch interference
const isUpdating = ref(false)

// Dynamic placeholder based on selected country
const dynamicPlaceholder = computed(() => {
  const code = getCountryCode(selectedCountry.value)
  // Get example number format for the country
  try {
    const formatter = new AsYouType(selectedCountry.value)
    // Generate example with country-specific format
    const exampleDigits = selectedCountry.value === 'US' ? '5550001234' : '1234567890'
    const example = formatter.input(exampleDigits)
    return `${code} ${example.replace(code, '').trim()}`
  } catch {
    return `${code} (555) 000-0000`
  }
})

// Popular countries to show first
const popularCountries: CountryCode[] = ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'ES', 'IT', 'JP', 'CN']

// All available countries
const allCountries = computed(() => {
  return getCountries()
})

// Popular countries list
const popularCountryList = computed(() => {
  return popularCountries.filter(code => allCountries.value.includes(code))
})

// Other countries (not in popular list)
const otherCountries = computed(() => {
  return allCountries.value.filter(code => !popularCountries.includes(code))
})

// Get country calling code with +
const getCountryCode = (country: CountryCode) => {
  return `+${getCountryCallingCode(country)}`
}

// Get country flag emoji
const getCountryFlag = (country: CountryCode) => {
  const codePoints = country
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

// Get country name
const getCountryName = (country: CountryCode) => {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
  return regionNames.of(country) || country
}

// Filter countries based on search query
const filterCountries = (list: CountryCode[], search: string) => {
  if (!search) return list

  const searchLower = search.toLowerCase()
  return list.filter(country => {
    const name = getCountryName(country).toLowerCase()
    const code = getCountryCode(country).toLowerCase()
    return name.includes(searchLower) || code.includes(searchLower) || country.toLowerCase().includes(searchLower)
  })
}

// Select a country
const selectCountry = (country: CountryCode) => {
  selectedCountry.value = country
  countryPickerOpen.value = false
}

/**
 * Format and update phone number value
 * @param value - Raw phone number string to format
 */
const formatAndUpdateValue = (value: string | number) => {
  // Convert to string if number is provided
  value = String(value)
  const countryCode = getCountryCode(selectedCountry.value)
  const minLength = countryCode.length + 1 // +1 for space

  // If user deletes the country code prefix, restore it
  if (!value.startsWith(countryCode)) {
    value = countryCode + ' ' + value.replace(/^\+?\d+\s*/, '')
  }

  // Don't allow deleting below the country code
  if (value.length < minLength) {
    value = countryCode + ' '
  }

  // Use AsYouType for formatting (lightweight operation)
  const formatter = new AsYouType(selectedCountry.value)
  const formatted = formatter.input(value)

  // Set flag to prevent watch interference
  isUpdating.value = true

  // Update local state and emit immediately
  phoneInput.value = formatted
  emit('update:modelValue', formatted)

  // Clear flag after update completes
  setTimeout(() => {
    isUpdating.value = false
  }, 0)
}

/**
 * Handle native input event
 */
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  formatAndUpdateValue(target.value)
}

// Handle focus - place cursor after country code
const handleFocus = (event: Event) => {
  const target = event.target as HTMLInputElement
  const countryCode = getCountryCode(selectedCountry.value)

  // If field is empty, set country code
  if (!phoneInput.value || phoneInput.value.trim() === '') {
    phoneInput.value = countryCode + ' '
    emit('update:modelValue', phoneInput.value)
  }

  // Position cursor at end
  setTimeout(() => {
    target.setSelectionRange(phoneInput.value.length, phoneInput.value.length)
  }, 0)
}

// Handle key down to prevent deleting country code
const handleKeyDown = (event: KeyboardEvent) => {
  const target = event.target as HTMLInputElement
  const countryCode = getCountryCode(selectedCountry.value)
  const minLength = countryCode.length + 1

  const cursorPosition = target.selectionStart || 0

  // Prevent backspace/delete if cursor is within country code area
  if ((event.key === 'Backspace' || event.key === 'Delete') && cursorPosition <= minLength) {
    event.preventDefault()
  }
}

// Sync modelValue from parent (vee-validate) to local state
watch(() => props.modelValue, (newValue) => {
  // Don't update if user is currently typing to prevent interference
  if (isUpdating.value) return

  if (newValue !== undefined && newValue !== phoneInput.value) {
    phoneInput.value = newValue
  }
})

// Update display when country changes
watch(selectedCountry, (newCountry, oldCountry) => {
  const newCode = getCountryCode(newCountry)
  const oldCode = oldCountry ? getCountryCode(oldCountry) : ''

  if (phoneInput.value && oldCode) {
    // Replace old country code with new one
    let numberWithoutCode = phoneInput.value
    if (phoneInput.value.startsWith(oldCode)) {
      numberWithoutCode = phoneInput.value.substring(oldCode.length).trim()
    }

    // Apply new country code and format
    const formatter = new AsYouType(newCountry)
    const newValue = newCode + ' ' + numberWithoutCode
    phoneInput.value = formatter.input(newValue)
    emit('update:modelValue', phoneInput.value)
  } else if (!oldCountry) {
    // On initial mount, set country code (don't emit to avoid interference)
    phoneInput.value = newCode + ' '
  }
}, { immediate: true })
</script>

<template>
  <div :class="cn('flex gap-2', props.class)">
    <!-- Country Selector -->
    <Popover v-model:open="countryPickerOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          role="combobox"
          :disabled="disabled"
          :aria-expanded="countryPickerOpen"
          class="w-[80px] justify-between px-3"
        >
          <span class="text-2xl">{{ getCountryFlag(selectedCountry) }}</span>
          <ChevronsUpDown class="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-[300px] p-0" align="start">
        <Command :filter-function="filterCountries">
          <CommandInput placeholder="Search country..." />
          <CommandList class="max-h-[300px]">
            <CommandEmpty>No country found.</CommandEmpty>

            <!-- Popular Countries -->
            <CommandGroup heading="Popular">
              <CommandItem
                v-for="country in popularCountryList"
                :key="`popular-${country}`"
                :value="country"
                @select="selectCountry(country)"
                class="cursor-pointer"
              >
                <span class="flex items-center gap-2">
                  <span class="text-lg">{{ getCountryFlag(country) }}</span>
                  <span class="text-sm font-medium">{{ getCountryCode(country) }}</span>
                  <span class="text-sm text-gray-600">{{ getCountryName(country) }}</span>
                </span>
              </CommandItem>
            </CommandGroup>

            <!-- All Other Countries -->
            <CommandGroup heading="All Countries">
              <CommandItem
                v-for="country in otherCountries"
                :key="country"
                :value="country"
                @select="selectCountry(country)"
                class="cursor-pointer"
              >
                <span class="flex items-center gap-2">
                  <span class="text-lg">{{ getCountryFlag(country) }}</span>
                  <span class="text-sm font-medium">{{ getCountryCode(country) }}</span>
                  <span class="text-sm text-gray-600">{{ getCountryName(country) }}</span>
                </span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    <!-- Phone Number Input -->
    <Input
      type="tel"
      :model-value="phoneInput"
      :placeholder="dynamicPlaceholder"
      :disabled="disabled"
      @update:model-value="formatAndUpdateValue"
      @focus="handleFocus"
      @keydown="handleKeyDown"
      class="flex-1"
    />
  </div>
</template>
